import React from 'react';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';
import ImageComponent from 'shared/ImageComponent';
import { dollarFormat } from 'utils/utils';
import { convertDateFromApiToUserTimezone } from 'utils/utility_functions/timezone';
import constants from 'constants/index';
import GlobalTooltip from 'components/GlobalTooltip';

/**
 * Case/Lead column body renderer
 * @param {Object} params - Parameters object
 * @param {Object} params.rowData - Row data
 * @returns {JSX.Element} - Case/Lead cell
 */
export const caseBody = (params) => {
  const { rowData } = params || {};
  if (!rowData) {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Case</span>
        <span>-</span>
      </React.Fragment>
    );
  }
  const { case_id, is_lead } = rowData || {};
  const mainModuleName = is_lead ? '/leads' : '/cases';
  let route = case_id ? `${mainModuleName}/${case_id}` : mainModuleName;

  return (
    <React.Fragment>
      <span className="p-column-title text-break">Case</span>
      <Link to={route} className="redirection-link-no-text-transform" title={rowData.case_name ? rowData.case_name : undefined}>
        {rowData.case_name ? rowData.case_name : '-'}
      </Link>
    </React.Fragment>
  );
};

/**
 * Type column body renderer
 * @param {Object} params - Parameters object
 * @param {Object} params.rowData - Row data
 * @returns {JSX.Element} - Type cell
 */
export const typeBody = (params) => {
  const { rowData } = params || {};
  if (!rowData) {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Type</span>
        <span className="text-break">-</span>
      </React.Fragment>
    );
  }
  return (
    <React.Fragment>
      <span className="p-column-title text-break">Type</span>
      <span className="text-break" title={rowData.expense_type}>
        {rowData.expense_type || '-'}
      </span>
    </React.Fragment>
  );
};

/**
 * Team Member column body renderer
 * @param {Object} params - Parameters object
 * @param {Object} params.rowData - Row data
 * @param {string} params.tenantId - Tenant ID
 * @returns {JSX.Element} - Team Member cell
 */
export const teamMemberBody = (params) => {
  const { rowData, tenantId } = params || {};
  if (!rowData) {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Team Member</span>
        <span>-</span>
      </React.Fragment>
    );
  }
  const firmUserId = rowData?.team_member_id || rowData?.firm_user_id;
  return (
    <Link
      className="d-inline-flex redirection-link-no-text-transform"
      to={firmUserId ? `/settings/team-management/${firmUserId}` : '/settings/team-management'}
    >
      <span className="p-column-title text-break">Team Member</span>
      <span className="d-flex">
        <ImageComponent filePath={`${tenantId}/${firmUserId}/profile_image`} fileName={rowData?.team_member_name} fileSize="small.jpg" />
        <span
          className="redirection-link-no-text-transform"
          title={rowData?.first_name}
          style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            width: '180px',
          }}
        >
          {firmUserId && <i className="fas fa-user-tie me-2"></i>}
          {rowData?.first_name ? `${rowData?.first_name} ${rowData?.last_name}` : '-'}
        </span>
      </span>
    </Link>
  );
};

/**
 * Status column body renderer
 * @param {Object} params - Parameters object
 * @param {Object} params.rowData - Row data
 * @returns {JSX.Element} - Status cell
 */
export const statusBody = (params) => {
  const { rowData } = params || {};
  if (!rowData) {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Status</span>
        <span className="text-break">-</span>
      </React.Fragment>
    );
  }
  let status = '';
  if (rowData?.expense_status === 'UNINVOICED') {
    status = 'Uninvoiced';
  } else {
    status = 'Invoiced';
  }
  return (
    <React.Fragment>
      <span className="p-column-title text-break">Status</span>
      <span className="text-break" title={status}>
        {status}
      </span>
    </React.Fragment>
  );
};

/**
 * Total Amount column body renderer
 * @param {Object} params - Parameters object
 * @param {Object} params.rowData - Row data
 * @returns {JSX.Element} - Total Amount cell
 */
export const totalAmountBody = (params) => {
  const { rowData } = params || {};
  if (!rowData) {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Total Amount</span>
        <span className="text-break">-</span>
      </React.Fragment>
    );
  }
  return (
    <React.Fragment>
      <span className="p-column-title text-break">Total Amount</span>
      <span className="text-break" title={dollarFormat(rowData.total_amount)}>
        {dollarFormat(rowData.total_amount)}
      </span>
    </React.Fragment>
  );
};

/**
 * Date column body renderer
 * @param {Object} params - Parameters object
 * @param {Object} params.rowData - Row data
 * @param {string} params.timezone - User timezone
 * @returns {JSX.Element} - Date cell
 */
export const dateBody = (params) => {
  const { rowData, timezone } = params || {};
  if (!rowData) {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Date</span>
        <span className="text-break">-</span>
      </React.Fragment>
    );
  }
  const formattedDate = convertDateFromApiToUserTimezone(rowData.expense_date, timezone, constants.month_date_year_format);
  return (
    <React.Fragment>
      <span className="p-column-title text-break">Date</span>
      {rowData.expense_date ? (
        <span className="text-break" title={formattedDate !== '-' ? formattedDate : undefined}>
          {formattedDate}
        </span>
      ) : (
        'N/A'
      )}
    </React.Fragment>
  );
};

/**
 * Action column body renderer
 * @param {Object} params - Parameters object
 * @param {Object} params.rowData - Row data
 * @param {Function} params.onEdit - Edit handler function
 * @param {Function} params.onDelete - Delete handler function
 * @returns {JSX.Element} - Action cell
 */
export const actionBody = (params) => {
  const { rowData, onEdit, onDelete } = params || {};
  return (
    <React.Fragment>
      <span className="p-column-title text-break">Action</span>
      <span role="button">
        <GlobalTooltip tooltip="Edit" className="ms-2">
          <i className="icon-edit cursor-pointer" onClick={() => onEdit?.(rowData)} id="edit" />
        </GlobalTooltip>
      </span>
      <span role="button">
        <GlobalTooltip tooltip="Delete" className="ms-4 ">
          <i className="icon-delete cursor-pointer" onClick={() => onDelete?.(rowData)} id="delete" />
        </GlobalTooltip>
      </span>
    </React.Fragment>
  );
};
